import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CheckCircle2, PawPrint, Heart, Calendar as CalendarIcon } from "lucide-react";
import { api, SERVICES, ADDONS } from "@/lib/api";
import DatePicker from "@/components/DatePicker";

export default function BookPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: form, 2: review+pay
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);

  const [form, setForm] = useState({
    client_name: "",
    email: "",
    phone: "",
    pet_name: "",
    pet_type: "Dog",
    pet_notes: "",
    service: SERVICES[1].id,
    addons: [],
    start_date: "",
    end_date: "",
    quantity: 1,
    message: "",
  });

  useEffect(() => {
    api.get("/config").then((r) => setPaypalClientId(r.data.paypal_client_id)).catch(() => {});
  }, []);

  const selectedService = useMemo(
    () => SERVICES.find((s) => s.id === form.service) || SERVICES[0],
    [form.service]
  );

  const estimatedTotal = useMemo(() => {
    const base = selectedService.price * Math.max(1, Number(form.quantity) || 1);
    const flatAdds = ADDONS.filter(
      (a) => a.kind === "flat" && form.addons.includes(a.id)
    ).reduce((s, a) => s + a.price, 0);
    const percentAdds = ADDONS.filter(
      (a) => a.kind === "percent" && form.addons.includes(a.id)
    ).reduce((s, a) => s + a.price, 0);
    return Math.round((base + flatAdds) * (1 + percentAdds) * 100) / 100;
  }, [selectedService, form.addons, form.quantity]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleAddon = (id) =>
    setForm((f) => ({
      ...f,
      addons: f.addons.includes(id)
        ? f.addons.filter((x) => x !== id)
        : [...f.addons, id],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.email || !form.phone || !form.pet_name || !form.start_date) {
      toast.error("Please fill in your name, contact info, pet name, and a start date.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        client_name: form.client_name,
        email: form.email,
        phone: form.phone,
        pet_name: form.pet_name,
        pet_type: form.pet_type,
        pet_notes: form.pet_notes,
        service: selectedService.label,
        addons: form.addons.map((id) => ADDONS.find((a) => a.id === id).label),
        start_date: form.start_date,
        end_date: form.end_date,
        estimated_total: estimatedTotal,
        message: form.message,
      };
      const { data } = await api.post("/bookings", payload);
      setBooking(data);
      setStep(2);
      toast.success("Booking request received! Aurora will reach out to confirm.");
    } catch (err) {
      toast.error("Couldn't submit booking. Please try again or call Aurora directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-page" className="relative py-16 sm:py-24">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <header className="text-center mb-12">
          <span className="font-script text-2xl text-[#b85b7e]">book a visit</span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-[#3F2A56] mt-2 leading-tight">
            Let&rsquo;s plan your pet&rsquo;s happy place.
          </h1>
          <p className="mt-4 text-[#5C4A73] text-lg max-w-2xl mx-auto leading-relaxed">
            Submit your request below. Aurora reviews every booking personally
            and will confirm by phone or email within 24 hours.
          </p>
        </header>

        {step === 1 && (
          <form
            data-testid="booking-form"
            onSubmit={onSubmit}
            className="parchment-card rounded-3xl p-7 sm:p-10 space-y-8"
          >
            <Section title="About you">
              <Grid>
                <Field label="Your name" required>
                  <input
                    data-testid="input-client-name"
                    required
                    value={form.client_name}
                    onChange={(e) => update("client_name", e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    data-testid="input-phone"
                    required
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    data-testid="input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className={inputCls}
                  />
                </Field>
              </Grid>
            </Section>

            <Section title="About your pet">
              <Grid>
                <Field label="Pet name" required>
                  <input
                    data-testid="input-pet-name"
                    required
                    value={form.pet_name}
                    onChange={(e) => update("pet_name", e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Pet type">
                  <select
                    data-testid="select-pet-type"
                    value={form.pet_type}
                    onChange={(e) => update("pet_type", e.target.value)}
                    className={inputCls}
                  >
                    <option>Dog</option>
                    <option>Cat</option>
                    <option>Multiple pets</option>
                    <option>Other</option>
                  </select>
                </Field>
              </Grid>
              <Field label="Anything Aurora should know? (allergies, routine, favorite spots…)">
                <textarea
                  data-testid="input-pet-notes"
                  rows={3}
                  value={form.pet_notes}
                  onChange={(e) => update("pet_notes", e.target.value)}
                  className={inputCls}
                />
              </Field>
            </Section>

            <Section title="Choose your service">
              <div className="grid sm:grid-cols-2 gap-3">
                {SERVICES.map((s) => (
                  <label
                    key={s.id}
                    data-testid={`service-option-${s.id}`}
                    className={`flex items-center justify-between gap-3 p-4 rounded-2xl cursor-pointer border-2 transition-all ${
                      form.service === s.id
                        ? "bg-[#3F2A56] text-[#F4E8C8] border-[#3F2A56]"
                        : "bg-[#F4E8C8]/60 text-[#3F2A56] border-[#3F2A56]/20 hover:border-[#3F2A56]/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="service"
                        value={s.id}
                        checked={form.service === s.id}
                        onChange={() => update("service", s.id)}
                        className="sr-only"
                      />
                      <PawPrint className="w-5 h-5 shrink-0" />
                      <div>
                        <div className="font-display font-semibold leading-tight">
                          {s.label}
                        </div>
                        <div
                          className={`text-xs ${
                            form.service === s.id ? "text-[#F4E8C8]/80" : "text-[#5C4A73]"
                          }`}
                        >
                          {s.category}
                        </div>
                      </div>
                    </div>
                    <div className="font-display text-xl font-bold">${s.price}</div>
                  </label>
                ))}
              </div>
            </Section>

            <Section title="Add-ons (optional)">
              <div className="grid sm:grid-cols-3 gap-3">
                {ADDONS.map((a) => {
                  const active = form.addons.includes(a.id);
                  return (
                    <button
                      type="button"
                      key={a.id}
                      data-testid={`addon-toggle-${a.id}`}
                      onClick={() => toggleAddon(a.id)}
                      className={`p-4 rounded-2xl border-2 text-left transition-all ${
                        active
                          ? "bg-[#4A6741] text-[#F4E8C8] border-[#4A6741]"
                          : "bg-[#F4E8C8]/60 text-[#3F2A56] border-[#3F2A56]/15 hover:border-[#3F2A56]/40"
                      }`}
                    >
                      <div className="font-display font-semibold">{a.label}</div>
                      <div className={`text-sm ${active ? "text-[#F4E8C8]/80" : "text-[#5C4A73]"}`}>
                        {a.kind === "flat" ? `+$${a.price}` : `+${a.price * 100}%`}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Section>

            <Section title="When?">
              <Grid>
                <Field label="Start date" required>
                  <DatePicker
                    testid="input-start-date"
                    value={form.start_date}
                    onChange={(v) => update("start_date", v)}
                    placeholder="Choose a start date"
                  />
                </Field>
                <Field label="End date (for sitting/multi-day)">
                  <DatePicker
                    testid="input-end-date"
                    value={form.end_date}
                    onChange={(v) => update("end_date", v)}
                    placeholder="Optional end date"
                  />
                </Field>
                <Field label="Number of visits / days">
                  <input
                    data-testid="input-quantity"
                    type="number"
                    min={1}
                    value={form.quantity}
                    onChange={(e) => update("quantity", e.target.value)}
                    className={inputCls}
                  />
                </Field>
              </Grid>
              <Field label="Anything else?">
                <textarea
                  data-testid="input-message"
                  rows={3}
                  placeholder="Special requests, schedule notes, household tasks…"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className={inputCls}
                />
              </Field>
            </Section>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 pt-2 border-t-2 border-dashed border-[#3F2A56]/20">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#5C4A73]">
                  Estimated total
                </div>
                <div
                  data-testid="estimated-total"
                  className="font-display text-4xl font-bold text-[#3F2A56]"
                >
                  ${estimatedTotal.toFixed(2)}
                </div>
                <div className="text-xs text-[#5C4A73] mt-1">
                  Final total confirmed after Aurora reviews your request.
                </div>
              </div>
              <button
                type="submit"
                data-testid="submit-booking-btn"
                disabled={submitting}
                className="rounded-full bg-[#3F2A56] hover:bg-[#523870] text-[#F4E8C8] font-semibold px-8 py-4 text-base transition-all hover:-translate-y-0.5 shadow-md disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Submit booking request"}
              </button>
            </div>
          </form>
        )}

        {step === 2 && booking && (
          <ConfirmStep
            booking={booking}
            paypalClientId={paypalClientId}
            onDone={() => navigate("/")}
          />
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-2xl bg-[#FFFBF0] border-2 border-[#3F2A56]/20 px-4 py-3 text-[#2A1C3A] focus:outline-none focus:ring-2 focus:ring-[#4A6741] focus:border-[#4A6741] transition-colors placeholder:text-[#5C4A73]/50";

function Section({ title, children }) {
  return (
    <div className="space-y-4">
      <h3 className="font-display text-2xl font-semibold text-[#3F2A56] flex items-center gap-2">
        <Heart className="w-4 h-4 fill-[#b85b7e] text-[#b85b7e]" />
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
}
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-[#2A1C3A] mb-1.5">
        {label} {required && <span className="text-[#b85b7e]">*</span>}
      </span>
      {children}
    </label>
  );
}

function ConfirmStep({ booking, paypalClientId, onDone }) {
  const [paid, setPaid] = useState(booking.status === "paid");

  return (
    <div data-testid="confirm-step" className="parchment-card rounded-3xl p-7 sm:p-10">
      <div className="flex items-center gap-3 mb-3">
        <CheckCircle2 className="w-7 h-7 text-[#4A6741]" />
        <h2 className="font-display text-3xl font-bold text-[#3F2A56]">
          Request received!
        </h2>
      </div>
      <p className="text-[#5C4A73] leading-relaxed">
        Thank you, <strong className="text-[#3F2A56]">{booking.client_name}</strong>!
        Aurora has received your request for{" "}
        <strong className="text-[#3F2A56]">{booking.pet_name}</strong> and will
        confirm by phone/email shortly. A confirmation copy is on its way to{" "}
        <strong className="text-[#3F2A56]">{booking.email}</strong>.
      </p>

      <dl className="mt-6 grid sm:grid-cols-2 gap-4 p-5 rounded-2xl bg-[#F4E8C8]/60 border border-[#3F2A56]/15 text-sm">
        <Detail label="Service" value={booking.service} />
        <Detail label="Start date" value={booking.start_date} icon={<CalendarIcon className="w-3.5 h-3.5" />} />
        {booking.end_date && <Detail label="End date" value={booking.end_date} />}
        <Detail label="Estimated total" value={`$${booking.estimated_total.toFixed(2)}`} />
        {booking.addons.length > 0 && (
          <Detail label="Add-ons" value={booking.addons.join(", ")} />
        )}
        <Detail label="Booking ID" value={booking.id.slice(0, 8)} />
      </dl>

      <div className="mt-8">
        <h3 className="font-display text-2xl font-semibold text-[#3F2A56] mb-2">
          Pay your deposit (optional)
        </h3>
        <p className="text-[#5C4A73] mb-4 leading-relaxed">
          You can pay securely now via PayPal, or wait until Aurora confirms and
          sends a final invoice — totally up to you.
        </p>

        {paid ? (
          <div
            data-testid="payment-success"
            className="p-5 rounded-2xl bg-[#E8F0E6] border-2 border-[#4A6741]/30 text-[#2A1C3A] flex items-center gap-3"
          >
            <CheckCircle2 className="w-6 h-6 text-[#4A6741]" />
            <span className="font-semibold">
              Payment received! Aurora will reach out to confirm details.
            </span>
          </div>
        ) : paypalClientId ? (
          <PayPalScriptProvider
            options={{ clientId: paypalClientId, currency: "USD" }}
          >
            <div data-testid="paypal-buttons-wrapper">
              <PayPalButtons
                style={{ layout: "vertical", shape: "pill", color: "gold" }}
                createOrder={async () => {
                  const { data } = await api.post("/paypal/create-order", {
                    booking_id: booking.id,
                    amount: booking.estimated_total,
                  });
                  return data.order_id;
                }}
                onApprove={async (data) => {
                  const res = await api.post("/paypal/capture-order", {
                    booking_id: booking.id,
                    order_id: data.orderID,
                  });
                  if (res.data.status === "COMPLETED") {
                    setPaid(true);
                    toast.success("Payment complete! Thank you 💛");
                  } else {
                    toast.error("Payment didn't complete. Please try again.");
                  }
                }}
                onError={() =>
                  toast.error("PayPal error. You can also pay after Aurora confirms.")
                }
              />
            </div>
          </PayPalScriptProvider>
        ) : (
          <div
            data-testid="paypal-pending"
            className="p-5 rounded-2xl bg-[#F4E8C8]/70 border-2 border-dashed border-[#3F2A56]/30 text-[#5C4A73]"
          >
            <p className="font-semibold text-[#3F2A56] mb-1">PayPal is being set up.</p>
            <p>
              No worries — Aurora will reach out via email with payment options
              once your booking is confirmed.
            </p>
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <button
          onClick={onDone}
          data-testid="confirm-done-btn"
          className="rounded-full bg-[#3F2A56] hover:bg-[#523870] text-[#F4E8C8] font-semibold px-6 py-3"
        >
          Back to home
        </button>
        {!paid && (
          <button
            type="button"
            onClick={onDone}
            data-testid="pay-later-link"
            className="text-[#3F2A56] underline underline-offset-4 decoration-dotted text-sm font-medium hover:text-[#523870]"
          >
            I&rsquo;ll pay the deposit later
          </button>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value, icon }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-[#5C4A73]">{label}</dt>
      <dd className="font-display text-lg font-semibold text-[#3F2A56] flex items-center gap-1.5">
        {icon}
        {value}
      </dd>
    </div>
  );
}
